import {
  addDays,
  addHours,
  addMinutes,
  differenceInMinutes,
  endOfDay,
  format,
  isLastDayOfMonth,
  parse,
  startOfDay,
} from "date-fns";
import { ru } from "date-fns/locale";
import { computed, Ref } from "vue";
import store from "@/store";
import { FrvQuery, TimeShiftQuery } from '@/types';

const breakTime = 1;
const breakTime2 = 0.5;
const hoursToFirstBreak = 4; // начало первого обеда
const hoursToSecondBreak = 9; // начало второго обеда

const calcResult: FrvQuery = {
  frv: 0,
  frvRV: 0
}

// Праздники главного экрана + праздники модалки Беру (могут быть за следующий месяц)
const isHolidayOnDate = computed(() => (date: string) => {
  const holidays = [
    ...(store.getters["data/holidays"] || []),
    ...(store.getters["data/beruHolidays"] || []),
  ];
  return holidays.includes(date);
});

// Принимаем строку со временем в формате 00:00 и отдаём количество секунд
const timeToSeconds = (time: string): number => {
  const hours: number = +time.split(":")[0];
  const minutes: number = +time.split(":")[1];
  return (hours * 60 + minutes) * 60;
};

// Проверка, что следующий день праздничный
const checkNextDayHoliday = (dateFrom: string) => {
  if (dateFrom) {
    const date1 = new Date(dateFrom)
    const date2 = new Date(addDays(new Date(dateFrom), 1).toISOString())

    const areDatesNotEqual = date1.getTime() !== date2.getTime();

    const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
    const isDifferenceNotMoreThanOneDay = Math.abs(date2.getTime() - date1.getTime()) <= oneDayInMilliseconds;

    if (areDatesNotEqual && isDifferenceNotMoreThanOneDay) {
      return isHolidayOnDate.value(`${date2.getUTCDate()}.${date2.getUTCMonth() + 1}.${date2.getUTCFullYear()}`);
    }
  }
}

// Просчёт обедов пересекающих сутки
const calcPassingDinner = (endDayTime: number, endTime: number | Date, startTime: number | Date, inverted = false, alternate = false, oneDinner = false, timeShiftQuery) => {
  /*
  Переводим endDayTime в секунды
  получаем кол-во минут обеда в текущем дне и добавляем 1 секунду, которая не учитывается в 23:59
  Вычисляем ФРВ по формуле: endDayTime - breakTime - differenceStart и получаем кол-во секунд.
  Вычисляем ФРВ РВ по формуле: timeShiftQuery.timeShiftFull - frv и получаем кол-во часов для ФРВ РВ

  Параметр inverted меняет местами ФРВ и ФРВ РВ
  Параметр alternate и oneDinner меняет алгоритм подсчёта
  * */

  const startOfDay1 = startOfDay(endTime)
  const endOfDay2 = endOfDay(startTime)

  let frv = 0;
  let frvRV = 0;

  const differenceStart = differenceInMinutes(endOfDay2, startTime) + 1
  const differenceEnd = differenceInMinutes(endTime, startOfDay1)

  if (differenceStart > 90 || differenceEnd > 90) {
    console.warn('Превышение общего времени обеда')
  }

  if (alternate) {
    frvRV = ((endDayTime * 60) - differenceStart) / 60
    frv = ((timeShiftQuery.timeShiftFull * 60) - (frvRV * 60)) / 60
  } else if (!alternate && !oneDinner) {
    // console.log('другая формула расчёта, учитываем длительность первого обеда');
    frvRV = (((endDayTime * 60) - 60) - differenceStart) / 60
    frv = ((timeShiftQuery.timeShiftFull * 60) - (frvRV * 60)) / 60
  }

  if (oneDinner) {
    frvRV = ((endDayTime * 60) - differenceStart) / 60
    frv = ((timeShiftQuery.timeShiftFull * 60) - (frvRV * 60)) / 60
  }

  if (!inverted) {
    calcResult.frv = frvRV
    calcResult.frvRV = frv
  } else {
    calcResult.frv = frv
    calcResult.frvRV = frvRV
  }

  // console.log('Результат: ', frv, frvRV);
}

const formatOneDigitTime = (dateString: number | Date) => {
  const date = new Date(dateString);
  const hours = date.getHours();
  const minutes = date.getMinutes();

  // Форматируем часы или минуты если значение меньше 10
  const formattedHours = hours < 10 ? `0${hours}` : hours.toString();
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes.toString();

  return `${formattedHours}:${formattedMinutes}`;
}

// Проверка на следующий день, если checkMidnight: true - то считаем 24:00 в текущем дне
const isNextDay = (timeFromDate: string, dateString: Date | number, checkMidnight = false, timeFrom) => {
  if (dateString.toString() === 'Invalid Date') {
    return false
  }

  if (!timeFromDate || dateString === 0) {
    return false
  }

  const date = `${new Date(dateString.toString()).getDate()}.${new Date(dateString.toString()).getMonth() + 1}.${new Date(dateString.toString()).getFullYear()}`
  const dateTime = formatOneDigitTime(dateString)

  const formattedDate2 = format(parse(date, 'dd.MM.yyyy', new Date(), { locale: ru }), 'yyyy-MM-dd', { locale: ru });
  const formattedDate = timeFromDate.split("T")[0]


  const res = `${formattedDate}T${timeFrom.value}`
  const res2 = `${formattedDate2}T${dateTime}`

  const current = new Date(res).getDate()
  const next = new Date(res2).getDate()

  if (checkMidnight) {
    if (dateTime === '00:00' && current !== next) {
      return false
    }
  }

  return current !== next;
}


// Расчёт обедов и длительности смены (перенос dinerCalculation из VitaShiftControl)
export const dinerCalculation = (
  timeShiftQuery: TimeShiftQuery,
  timeFrom: Ref<string>,
  timeTo: Ref<string>,
  date: Date,
  userTimeZone = 0,
): string => {
  const currentTimeShift = (timeToSeconds(timeTo.value) - timeToSeconds(timeFrom.value)) / 60 / 60;
  const isLastDay = isLastDayOfMonth(date);
  const timeToEndDay = 24 - timeToSeconds(timeFrom.value) / 60 / 60;

  const formattedDate = format(date, "yyyy-MM-dd", { locale: ru });
  const currentDate = new Date(`${formattedDate}T${timeFrom.value}`);

  let dinerTime = "";

  timeShiftQuery.timeShiftNoDiner = Math.sign(currentTimeShift) === -1 ? 24 + currentTimeShift : currentTimeShift;
  timeShiftQuery.timeShift = timeShiftQuery.timeShiftNoDiner;

  if (timeShiftQuery.timeShift < 5) {
    timeShiftQuery.timeShift = timeShiftQuery.timeShiftNoDiner;
    timeShiftQuery.timeShiftFull = timeShiftQuery.timeShiftNoDiner;
    timeShiftQuery.dinners.first.duration = 0;
    timeShiftQuery.dinners.second.duration = 0;
    timeShiftQuery.dinners.first.durationMinutes = 0;
    timeShiftQuery.dinners.second.durationMinutes = 0;
    dinerTime = "";
  } else if (timeShiftQuery.timeShift < 9) {
    timeShiftQuery.timeShift = timeShiftQuery.timeShiftNoDiner - breakTime2;
    timeShiftQuery.timeShiftFull = timeShiftQuery.timeShiftNoDiner - breakTime2;
    dinerTime = "30 минут";
    //
    timeShiftQuery.dinners.first.duration = 0.5;
    timeShiftQuery.dinners.first.durationMinutes = 30;
    timeShiftQuery.dinners.second.duration = 0;
    timeShiftQuery.dinners.second.durationMinutes = 0;
  } else if (timeShiftQuery.timeShift < 13) {
    dinerTime = "1 час";
    timeShiftQuery.timeShift = timeShiftQuery.timeShiftNoDiner - breakTime;
    timeShiftQuery.timeShiftFull = timeShiftQuery.timeShiftNoDiner - breakTime;
    //
    timeShiftQuery.dinners.first.duration = 1;
    timeShiftQuery.dinners.first.durationMinutes = 60;
    timeShiftQuery.dinners.second.duration = 0;
    timeShiftQuery.dinners.second.durationMinutes = 0;
  } else if (timeShiftQuery.timeShift >= 13) {
    dinerTime = "1 час 30 минут";

    timeShiftQuery.dinners.first.duration = 1;
    timeShiftQuery.dinners.second.duration = 0.5;
    timeShiftQuery.dinners.first.durationMinutes = 60;
    timeShiftQuery.dinners.second.durationMinutes = 30;

    timeShiftQuery.timeShift =
      timeShiftQuery.timeShiftNoDiner - breakTime2 - breakTime;
    timeShiftQuery.timeShiftFull =
      timeShiftQuery.timeShiftNoDiner - breakTime2 - breakTime;
  }

  if (timeShiftQuery.dinners.first.duration > 0) {
    const origin = addHours(currentDate, hoursToFirstBreak);
    timeShiftQuery.dinners.first.startTime = addHours(
      currentDate,
      hoursToFirstBreak
    );
    timeShiftQuery.dinners.first.endTime = addMinutes(
      origin,
      timeShiftQuery.dinners.first.durationMinutes
    );
  } else {
    timeShiftQuery.dinners.first.startTime = 0;
    timeShiftQuery.dinners.first.endTime = 0;
  }

  if (timeShiftQuery.dinners.second.duration > 0) {
    const origin = addHours(currentDate, hoursToSecondBreak);
    timeShiftQuery.dinners.second.startTime = addHours(
      currentDate,
      hoursToSecondBreak
    );
    timeShiftQuery.dinners.second.endTime = addMinutes(
      origin,
      timeShiftQuery.dinners.second.durationMinutes
    );
  } else {
    timeShiftQuery.dinners.second.startTime = 0;
    timeShiftQuery.dinners.second.endTime = 0;
  }

  if (
    isLastDay &&
    timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)
  ) {
    timeShiftQuery.timeShift = timeToEndDay;
    timeShiftQuery.timeShiftFull = timeToEndDay;
    if (
      timeShiftQuery.timeShiftNoDiner <= 9 &&
      timeShiftQuery.timeShiftNoDiner >= 5 &&
      timeToEndDay - userTimeZone > 0
    ) {
      timeShiftQuery.timeShift = timeToEndDay - breakTime2;
      timeShiftQuery.timeShiftFull = timeToEndDay - breakTime2;
    } else if (
      timeShiftQuery.timeShiftNoDiner < 13 &&
      timeShiftQuery.timeShiftNoDiner >= 5 &&
      timeToEndDay - userTimeZone > 0
    ) {
      timeShiftQuery.timeShift = timeToEndDay - breakTime;
      timeShiftQuery.timeShiftFull = timeToEndDay - breakTime;
    } else if (
      timeShiftQuery.timeShiftNoDiner >= 13 &&
      timeToEndDay - userTimeZone > 0 &&
      timeToEndDay - userTimeZone < 9
    ) {
      timeShiftQuery.timeShift = timeToEndDay - breakTime;
      timeShiftQuery.timeShiftFull = timeToEndDay - breakTime;
    } else if (timeShiftQuery.timeShiftNoDiner >= 13 && timeToEndDay - 9 >= 0) {
      timeShiftQuery.timeShift = timeToEndDay - breakTime - breakTime2;
      timeShiftQuery.timeShiftFull = timeToEndDay - breakTime - breakTime2;
    }
  }

  return dinerTime;
};

export const frvCore = (timeShiftQuery, isLastDay, isHoliday, shifts, timeFrom, timeTo) => {
  // Функция расчёта переходящих обедов и ФРВ РВ
  const dateFrom = shifts.value.dateFrom

  // количество часов до конца суток
  const endDayTime = 24 - timeToSeconds(timeFrom.value) / 60 / 60;

  const isNextDayHoliday = checkNextDayHoliday(dateFrom)
  // console.clear()

  if (timeShiftQuery.dinners.first.duration || timeShiftQuery.dinners.second.duration) {
    // console.log('[i] если есть первый или второй обед');

    if (!isHoliday.value && !isNextDayHoliday) {
      // console.log('[*] текущий и следующий день НЕ праздничный');
      calcResult.frvRV = 0
      calcResult.frv = timeShiftQuery.timeShiftFull
    }

    if (!isLastDay) { // не последний день месяца

      if (!isNextDay(dateFrom, timeShiftQuery.dinners.first.endTime, true, timeFrom)) { // конец обеда в текущем дне

        if (!isHoliday.value) {
          // console.log('[*] конец обеда в текущем дне (не праздничном дне).');

          if (timeShiftQuery.dinners.second.duration === 0) {
            // console.log('[i] нет второго обеда')
            if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)) {
              if (isNextDayHoliday) {
                if (timeShiftQuery.timeShiftNoDiner > endDayTime) {
                  calcResult.frvRV = timeShiftQuery.timeShiftNoDiner - endDayTime
                  calcResult.frv = timeShiftQuery.timeShiftFull - calcResult.frvRV
                } else {
                  calcResult.frvRV = 0
                  calcResult.frv = timeShiftQuery.timeShiftFull - calcResult.frvRV
                }
              } else {
                calcResult.frvRV = 0
                calcResult.frv = timeShiftQuery.timeShiftFull
              }
            } else {
              calcResult.frvRV = 0
              calcResult.frv = timeShiftQuery.timeShiftFull
            }
          }
        } else if (isHoliday.value) {
          // console.log('конец обеда в текущем праздничном дне');
          timeShiftQuery.holiday = true

          if (timeShiftQuery.dinners.second.duration === 0) {

            if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)) {
              if (!isNextDayHoliday) {
                if (timeShiftQuery.timeShiftNoDiner > endDayTime) {
                  calcResult.frv = timeShiftQuery.timeShiftNoDiner - endDayTime
                  calcResult.frvRV = timeShiftQuery.timeShiftFull - calcResult.frv
                } else {
                  if (timeShiftQuery.timeShiftFull > endDayTime) {
                    calcResult.frvRV = timeShiftQuery.timeShiftFull - endDayTime
                  } else {
                    calcResult.frvRV = timeShiftQuery.timeShiftFull - (endDayTime - timeShiftQuery.timeShiftNoDiner)
                  }

                  calcResult.frv = timeShiftQuery.timeShiftFull - calcResult.frvRV
                }
              } else {
                calcResult.frvRV = timeShiftQuery.timeShiftFull
                calcResult.frv = 0
              }

              // console.log('[*] новое условие для праздничного дня // второй обед отсутствует');
            } else {
              calcResult.frvRV = timeShiftQuery.timeShiftFull
              calcResult.frv = 0
              // console.log('[*] конец обеда в текущем праздничном дне // второй обед отсутствует');
            }

          } else {
            calcResult.frvRV = timeShiftQuery.timeShiftFull
            calcResult.frv = 0
            // console.log('[*] конец обеда в текущем праздничном дне // есть второй обед');
          }
        }

      } else if (isNextDay(dateFrom, timeShiftQuery.dinners.first.startTime, false, timeFrom) && (isHoliday.value && !isNextDayHoliday)) {
        // console.log('этот день праздничный, начало первого обеда в следующем дне (НЕ праздничном).');
        if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)) {
          calcResult.frvRV = endDayTime
          calcResult.frv = timeShiftQuery.timeShiftFull - calcResult.frvRV
        } else {
          timeShiftQuery.holiday = true

          calcResult.frvRV = timeShiftQuery.timeShiftFull
          calcResult.frv = 0
        }

      } else if (isNextDay(dateFrom, timeShiftQuery.dinners.first.startTime, false, timeFrom) && isNextDayHoliday) {
        // console.log('начало обеда в следующем дне (праздничном).');

        if (isHoliday.value) {
          timeShiftQuery.holiday = true
          calcResult.frvRV = timeShiftQuery.timeShiftFull
          calcResult.frv = 0
        } else {
          calcResult.frv = endDayTime
          calcResult.frvRV = timeShiftQuery.timeShiftFull - calcResult.frvRV
        }

      } else if (isNextDay(dateFrom, timeShiftQuery.dinners.first.startTime, false, timeFrom) && isHoliday.value) {
        // начало обеда в следующем праздничном дне
        // console.log('начало обеда в следующем праздничном дне');
      } else if (isNextDay(dateFrom, timeShiftQuery.dinners.first.startTime, false, timeFrom)) {
        // начало обеда в следующем дне
      } else {
        // всё что переходит между днями
        if (!isHoliday.value && !isNextDayHoliday) {
          // console.log('между днями, этот день не праздничный, следующий не праздничный');
          timeShiftQuery.holiday = false

          calcResult.frv = timeShiftQuery.timeShiftFull
          calcResult.frvRV = 0
        } else if (!isHoliday.value && isNextDayHoliday) {
          // console.log('между днями, этот день не праздничный, следующий праздничный');
          calcPassingDinner(endDayTime, timeShiftQuery.dinners.first.endTime, timeShiftQuery.dinners.first.startTime, false, false, true, timeShiftQuery)
        } else if (isHoliday.value && isNextDayHoliday) {
          // console.log('между днями, этот день праздничный и следующий праздничный');
          timeShiftQuery.holiday = true

          calcResult.frvRV = timeShiftQuery.timeShiftFull
          calcResult.frv = 0
        } else if (isHoliday.value && !isNextDayHoliday) {

          if (timeShiftQuery.dinners.second.duration === 0) {
            // console.log('[*] между днями, этот день праздничный, а следующий нет');
            calcPassingDinner(endDayTime, timeShiftQuery.dinners.first.endTime, timeShiftQuery.dinners.first.startTime, true, false, true, timeShiftQuery)
          }
        }
      }

      if (timeShiftQuery.dinners.second.duration > 0 && isHoliday.value) {
        // console.log('если есть второй обед и праздничный день')
        if ((!isNextDay(dateFrom, timeShiftQuery.dinners.first.endTime, true, timeFrom) && isNextDay(dateFrom, timeShiftQuery.dinners.second.startTime, false, timeFrom)) ||
          (!isNextDay(dateFrom, timeShiftQuery.dinners.first.endTime, false, timeFrom) && isNextDay(dateFrom, timeShiftQuery.dinners.second.startTime, true, timeFrom))) {

          if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)) {
            if (!isNextDayHoliday) {
              calcResult.frv = timeShiftQuery.timeShiftFull - endDayTime + breakTime
              calcResult.frvRV = timeShiftQuery.timeShiftFull - calcResult.frv
            }
            // console.log('[**] новое условие для праздничного дня // второй обед есть');
          }
          // console.log('[**] если (окончание первого обеда <= 24:00 И начало второго обеда > 00:00) ИЛИ (окончание первого обеда < 24:00 И начало второго обеда >= 00:00)');
        } else if (!isNextDay(dateFrom, timeShiftQuery.dinners.first.startTime, false, timeFrom)
          && isNextDay(dateFrom, timeShiftQuery.dinners.first.endTime, false, timeFrom)) {
          // первый обед начинается в текущем дне, а заканчивается в следующем
          if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)) {
            if (isHoliday.value && !isNextDayHoliday) {
              calcPassingDinner(endDayTime, timeShiftQuery.dinners.first.endTime, timeShiftQuery.dinners.first.startTime, true, true, false, timeShiftQuery) // расчёт праздничного дня на НЕ праздничный
            }
            // console.log('[**] новое условие для праздничного дня // второй обед есть');
          }

          // console.log('[**] если начало первого обеда < 24:00 И окончание первого обеда > 24:00');
        } else if (!isNextDay(dateFrom, timeShiftQuery.dinners.second.endTime, true, timeFrom)) { // оба обеда в текущем дне
          if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)) {
            if (!isNextDayHoliday) {
              calcResult.frv = timeShiftQuery.timeShiftNoDiner - endDayTime
              calcResult.frvRV = timeShiftQuery.timeShiftFull - calcResult.frv
            }
          }
          // console.log('[**] если окончание второго обеда <= 24:00');
        } else if (!isNextDay(dateFrom, timeShiftQuery.dinners.second.startTime, false, timeFrom)
          && isNextDay(dateFrom, timeShiftQuery.dinners.second.endTime, false, timeFrom)) { // первый обед и начало второго обеда в текущем дне, а конец второго обеда в следующем дне
          if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)) {
            if (isHoliday.value && !isNextDayHoliday) {
              calcPassingDinner(endDayTime, timeShiftQuery.dinners.second.endTime, timeShiftQuery.dinners.second.startTime, true, false, false, timeShiftQuery) // расчёт для праздничного дня
              // console.log('[**] делаем расчёт для переходящего обеда в праздничном дне // второй обед есть');
            }
          }

          // console.log('[**] если начало второго обеда < 24:00 И окончание второго обеда > 24:00');
        }

        if (isNextDay(dateFrom, timeShiftQuery.dinners.first.startTime, false, timeFrom)) { // оба обеда в следующем дне
          // console.log('[i] если начало первого обеда >= 24:00, текущий праздничный день');
        }

      } else if (timeShiftQuery.dinners.second.duration > 0 && !isHoliday.value) { // если есть второй обед и НЕ праздничный день
        // обратный код в условиях от блока с праздничным днём
        if ((!isNextDay(dateFrom, timeShiftQuery.dinners.first.endTime, true, timeFrom) && isNextDay(dateFrom, timeShiftQuery.dinners.second.startTime, false, timeFrom)) ||
          (!isNextDay(dateFrom, timeShiftQuery.dinners.first.endTime, false, timeFrom) && isNextDay(dateFrom, timeShiftQuery.dinners.second.startTime, true, timeFrom))) {

          if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)) {

            if (isNextDayHoliday) {
              calcResult.frvRV = timeShiftQuery.timeShiftFull - endDayTime + breakTime
              calcResult.frv = timeShiftQuery.timeShiftFull - calcResult.frvRV
            } else {
              calcResult.frvRV = 0
              calcResult.frv = timeShiftQuery.timeShiftFull
            }

            // console.log('[**] новое условие для праздничного дня // второй обед есть');
          }
          // console.log('[**] если (окончание первого обеда <= 24:00 И начало второго обеда > 00:00) ИЛИ (окончание первого обеда < 24:00 И начало второго обеда >= 00:00)');
        } else if (!isNextDay(dateFrom, timeShiftQuery.dinners.first.startTime, false, timeFrom)
          && isNextDay(dateFrom, timeShiftQuery.dinners.first.endTime, false, timeFrom)) { // первый обед начинается в текущем дне, а заканчивается в следующем
          if (isNextDayHoliday) {
            calcPassingDinner(endDayTime, timeShiftQuery.dinners.first.endTime, timeShiftQuery.dinners.first.startTime, false, true, false, timeShiftQuery)
          } else {
            calcResult.frvRV = 0
            calcResult.frv = timeShiftQuery.timeShiftFull
          }
          // console.log('[**] если начало первого обеда < 24:00 И окончание первого обеда > 24:00');
        } else if (!isNextDay(dateFrom, timeShiftQuery.dinners.second.endTime, true, timeFrom)) { // оба обеда в текущем дне
          if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)) {
            if (isNextDayHoliday) {
              calcResult.frvRV = timeShiftQuery.timeShiftNoDiner - endDayTime
              calcResult.frv = timeShiftQuery.timeShiftFull - calcResult.frvRV
            } else {
              calcResult.frvRV = 0
              calcResult.frv = timeShiftQuery.timeShiftFull
            }
          } else {
            calcResult.frvRV = 0
            calcResult.frv = timeShiftQuery.timeShiftFull
          }
          // console.log('[**] если окончание второго обеда <= 24:00');
        } else if (!isNextDay(dateFrom, timeShiftQuery.dinners.second.startTime, false, timeFrom)
          && isNextDay(dateFrom, timeShiftQuery.dinners.second.endTime, false, timeFrom)) { // первый обед и начало второго обеда в текущем дне, а конец второго обеда в следующем дне

          if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)) {
            if (isNextDayHoliday) {
              // второй обед в переходе между сутками
              calcPassingDinner(endDayTime, timeShiftQuery.dinners.second.endTime, timeShiftQuery.dinners.second.startTime, false, false, false, timeShiftQuery) // расчёт для НЕ праздничного дня
            } else {
              calcResult.frvRV = 0
              calcResult.frv = timeShiftQuery.timeShiftFull
            }

            // console.log('[**] новое условие для праздничного дня // второй обед есть');
          }

          // console.log('[**] если начало второго обеда < 24:00 И окончание второго обеда > 24:00');
        } else if (isNextDay(dateFrom, timeShiftQuery.dinners.first.startTime, false, timeFrom) && (!isHoliday.value && isNextDayHoliday)) {
          // console.log('начало обеда в следующем дне (НЕ праздничном), не делим обед потому что текущий день праздничный');

          calcResult.frv = endDayTime
          calcResult.frvRV = timeShiftQuery.timeShiftFull - calcResult.frv
        }

        if (isNextDay(dateFrom, timeShiftQuery.dinners.first.startTime, false, timeFrom)) { // оба обеда в следующем дне
          // console.log('[i] если начало первого обеда >= 24:00');
        }
      } else if (isNextDay(dateFrom, timeShiftQuery.dinners.first.startTime, false, timeFrom) && (!isHoliday.value && isNextDayHoliday)) {
        // console.log('[***] начало обеда в следующем в 00:01');

        if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)) {
          calcResult.frv = endDayTime
          calcResult.frvRV = timeShiftQuery.timeShiftFull - calcResult.frv
        }
      }
      //

    } else if (isLastDay) { // последний день месяца
      if (isHoliday.value) {
        // console.log('последний праздничный день месяца');
        calcResult.frv = 0
        calcResult.frvRV = timeShiftQuery.timeShiftFull

        if (timeShiftQuery.dinners.second.duration > 0 && !isNextDay(dateFrom, timeShiftQuery.dinners.second.endTime, true, timeFrom)) {
          // есть второй обед
          if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value) && timeShiftQuery.dinners.second.duration !== 0) {
            calcResult.frvRV = timeShiftQuery.timeShiftFull - breakTime2
            calcResult.frv = 0 // убираем расчёт ФРВ
          }
          // console.log('[**] если окончание второго обеда <= 24:00 в последнем праздничном дне месяца');
        } else if (!isNextDay(dateFrom, timeShiftQuery.dinners.first.startTime, false, timeFrom) && isNextDay(dateFrom, timeShiftQuery.dinners.first.endTime, false, timeFrom)) {
          // первый обед начинается в текущем дне, а заканчивается в следующем
          if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)) {
            calcPassingDinner(endDayTime, timeShiftQuery.dinners.first.endTime, timeShiftQuery.dinners.first.startTime, true, true, false, timeShiftQuery) // расчёт НЕ праздничного дня
            calcResult.frv = 0 // убираем расчёт ФРВ из calcPassingDinner()
          }

          if (timeShiftQuery.dinners.second.duration === 0) {
            calcPassingDinner(endDayTime, timeShiftQuery.dinners.first.endTime, timeShiftQuery.dinners.first.startTime, true, false, true, timeShiftQuery)
            calcResult.frv = 0 // убираем расчёт ФРВ из calcPassingDinner()
          }

          // console.log('[**] если начало первого обеда < 24:00 И окончание первого обеда > 24:00, последний день');
        } else if (!isNextDay(dateFrom, timeShiftQuery.dinners.second.startTime, false, timeFrom) && isNextDay(dateFrom, timeShiftQuery.dinners.second.endTime, false, timeFrom)) { // первый обед и начало второго обеда в текущем дне, а конец второго обеда в следующем дне
          if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)) {
            calcPassingDinner(endDayTime, timeShiftQuery.dinners.second.endTime, timeShiftQuery.dinners.second.startTime, true, false, false, timeShiftQuery) // расчёт для праздничного последнего дня
            calcResult.frv = 0 // убираем расчёт ФРВ из calcPassingDinner()
            // console.log('[**] делаем расчёт для переходящего обеда в праздничном последнем дне // второй обед есть');
          }

          // console.log('[**] если начало второго обеда < 24:00 И окончание второго обеда > 24:00');
        }

      } else {
        calcResult.frv = timeShiftQuery.timeShiftFull
        calcResult.frvRV = 0
        // console.log('последний НЕ праздничный день месяца');
        if (timeShiftQuery.dinners.second.duration > 0 && !isNextDay(dateFrom, timeShiftQuery.dinners.second.endTime, true, timeFrom)) { // оба обеда в текущем дне

          if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value) && timeShiftQuery.dinners.second.duration !== 0) {
            calcResult.frv = timeShiftQuery.timeShiftFull - breakTime2
            calcResult.frvRV = timeShiftQuery.timeShiftFull - calcResult.frv - breakTime2
          }


          // console.log('[**] если окончание второго обеда <= 24:00 в последнем дне месяца');
        } else if (!isNextDay(dateFrom, timeShiftQuery.dinners.first.startTime, false, timeFrom) && isNextDay(dateFrom, timeShiftQuery.dinners.first.endTime, false, timeFrom)) {
          // первый обед начинается в текущем дне, а заканчивается в следующем
          if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)) {
            calcPassingDinner(endDayTime, timeShiftQuery.dinners.first.endTime, timeShiftQuery.dinners.first.startTime, false, true, false, timeShiftQuery) // расчёт НЕ праздничного дня

            calcResult.frvRV = 0 // убираем расчёт ФРВ РВ из calcPassingDinner()
          }

          // console.log('[**] если начало первого обеда < 24:00 И окончание первого обеда > 24:00, последний день');
        } else if (!isNextDay(dateFrom, timeShiftQuery.dinners.second.startTime, false, timeFrom) && isNextDay(dateFrom, timeShiftQuery.dinners.second.endTime, false, timeFrom)) { // первый обед и начало второго обеда в текущем дне, а конец второго обеда в следующем дне
          if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)) {
            calcPassingDinner(endDayTime, timeShiftQuery.dinners.second.endTime, timeShiftQuery.dinners.second.startTime, false, false, false, timeShiftQuery) // расчёт для последнего дня
            calcResult.frvRV = 0 // убираем расчёт ФРВ РВ из calcPassingDinner()
            // console.log('[**] делаем расчёт для переходящего обеда в последнем дне // второй обед есть');
          }

          // console.log('[**] если начало второго обеда < 24:00 И окончание второго обеда > 24:00');
        }
      }
    }
  } else {
    // console.log('[i] если оба обеда отсутствуют');
    if (isLastDay && isHoliday.value) {
      calcResult.frv = 0
      calcResult.frvRV = timeShiftQuery.timeShiftFull
    }

    if (timeShiftQuery.timeShiftNoDiner < 1) {
      if (isHoliday.value) {
        calcResult.frv = 0
        calcResult.frvRV = timeShiftQuery.timeShiftFull
      } else {
        calcResult.frv = timeShiftQuery.timeShiftFull
        calcResult.frvRV = 0
      }
    }

    if (timeShiftQuery.timeShift < 5 && !isLastDay) {
      // console.log('менее 5 часов и обедов нет');
      if (isHoliday.value) {
        calcResult.frvRV = timeShiftQuery.timeShiftFull
        calcResult.frv = 0

        if (isNextDayHoliday) {
          calcResult.frvRV = timeShiftQuery.timeShiftFull
          calcResult.frv = 0
        } else if (!isNextDayHoliday) {
          if (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value)) {
            calcResult.frvRV = endDayTime
            calcResult.frv = timeShiftQuery.timeShiftNoDiner - endDayTime
          }
        }

        if (timeFrom.value === '00:00' && timeShiftQuery.timeShiftNoDiner <= 5) {
          calcResult.frvRV = timeShiftQuery.timeShiftNoDiner
          calcResult.frv = 0
        }
      } else {
        // console.log('менее 5 часов и не праздник');
        calcResult.frv = timeShiftQuery.timeShiftFull
        calcResult.frvRV = 0

        if (isNextDayHoliday && (timeToSeconds(timeTo.value) < timeToSeconds(timeFrom.value))) {
          calcResult.frvRV = timeShiftQuery.timeShiftNoDiner - endDayTime
          calcResult.frv = endDayTime
        }

        if (timeFrom.value === '00:00' && timeShiftQuery.timeShiftNoDiner <= 5) {
          calcResult.frvRV = 0
          calcResult.frv = timeShiftQuery.timeShiftNoDiner
        }
      }
    } else if (timeShiftQuery.timeShift < 5 && isLastDay) {
      if (isHoliday.value) {
        calcResult.frv = 0
        calcResult.frvRV = timeShiftQuery.timeShiftFull
      } else {
        calcResult.frv = timeShiftQuery.timeShiftFull
        calcResult.frvRV = 0
      }
    }
  }

  return calcResult;
}